import {
  dividePriceIntoBills,
  getCurvePointForMoneyAnimation,
  getPlayerListItemFromOrder,
  getBezierPoint,
} from "@/utils/animationHelpers"
import { useAppSelector, useAppDispatch } from "@/store/hooks"
import {
  removeMoneyAnimation,
  setMoneyAnimationAsStarted,
} from "@/store/globalSlice"
import { IMoneyAnimationItem, moneyAnimationItems } from "@/utils/data"
import gsap from "gsap"
import { useEffect, useRef, useState, useCallback, useMemo } from "react"
import { playMoneyAnimationSound } from "@/utils/audioManager"
import { activeMultiplier } from "@/utils/animationSpeed"

interface MoneyAnimationProps {
  animationData: IMoneyTransferAnimation
}

const MoneyAnimation: React.FC<MoneyAnimationProps> = ({ animationData }) => {
  const elements = useAppSelector((state) => state.global.elements)
  const moneyAnimations = useAppSelector(
    (state) => state.global.moneyAnimations
  )
  const dispatch = useAppDispatch()
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const moneyItemsRef = useRef<Array<HTMLDivElement | null>>([])
  const svgPathRef = useRef<SVGPathElement | null>(null)
  const [startPosition, setStartPosition] = useState({ x: 0, y: 0 })
  const [endPosition, setEndPosition] = useState({ x: 0, y: 0 })

  const fromOrder = animationData?.from
  const toOrder = animationData?.to

  useEffect(() => {
    const fromElement = document.querySelector(`#player-list-item-${fromOrder}`)
    const toElement = document.querySelector(`#player-list-item-${toOrder}`)

    if (!toElement || !fromElement) return
    const startPosition = fromElement?.getBoundingClientRect()
    const endPosition = toElement?.getBoundingClientRect()
    setStartPosition({
      x: startPosition?.x + startPosition.width / 2,
      y: startPosition?.y + startPosition.height / 2,
    })
    setEndPosition({
      x: endPosition?.x + endPosition.width / 2,
      y: endPosition?.y + endPosition.height / 2,
    })
  }, [elements.playerListItems, fromOrder, toOrder])

  // Memoize curve point calculation
  const curvePoint = useMemo(() => {
    if (
      !startPosition.x ||
      !startPosition.y ||
      !endPosition.x ||
      !endPosition.y
    ) {
      return { x: 0, y: 0 }
    }
    return getCurvePointForMoneyAnimation(
      startPosition.x,
      startPosition.y,
      endPosition.x,
      endPosition.y
    )
  }, [startPosition.x, startPosition.y, endPosition.x, endPosition.y])

  // Memoize path string for SVG (kept for visual reference, not used in animation)
  const pathString = useMemo(() => {
    if (
      !startPosition.x ||
      !startPosition.y ||
      !endPosition.x ||
      !endPosition.y
    ) {
      return ""
    }
    return `M${startPosition.x} ${startPosition.y} Q${curvePoint.x} ${curvePoint.y} ${endPosition.x} ${endPosition.y}`
  }, [
    startPosition.x,
    startPosition.y,
    endPosition.x,
    endPosition.y,
    curvePoint,
  ])

  const startMoneyAnimation = useCallback(() => {
    if (
      !startPosition.x ||
      !startPosition.y ||
      !endPosition.x ||
      !endPosition.y
    )
      return
    if (animationData.started) return

    dispatch(setMoneyAnimationAsStarted(animationData.id))

    // Respect users who prefer reduced motion: skip the flying-bills choreography
    // (the +/- flyoff still conveys the transfer) and clean up immediately.
    const prefersReducedMotion =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
    if (prefersReducedMotion) {
      dispatch(removeMoneyAnimation(animationData.id))
      return
    }

    const travelDuration = 1.15 * activeMultiplier()

    const promises = moneyItemsRef.current.map((item, i) => {
      if (!item) return Promise.resolve()

      return new Promise<void>((res) => {
        // A small, stable tilt per bill makes a batch read like a tossed
        // handful of cash instead of a rigid stack.
        const tilt = gsap.utils.random(-16, 16)

        // Use bezier curve calculation instead of motion path for better performance
        const timeline = gsap.timeline({
          delay: 0.12 * i * activeMultiplier(),
        })

        // Create a progress tracker object for bezier curve animation
        const progressTracker = { value: 0 }

        // Start tucked in and invisible so the bill "pops" out of the payer.
        timeline.set(item, {
          x: startPosition.x,
          y: startPosition.y,
          scale: 2,
          opacity: 0,
          rotation: 0,
          transformOrigin: "center center",
          // Force hardware acceleration
          force3D: true,
        })

        // Drive position, scale, opacity and rotation from a single progress
        // value so the whole envelope stays in sync along the bezier curve.
        timeline.to(progressTracker, {
          duration: travelDuration,
          value: 1,
          ease: "power2.inOut",
          onUpdate: function () {
            const p = progressTracker.value
            const point = getBezierPoint(
              startPosition.x,
              startPosition.y,
              curvePoint.x,
              curvePoint.y,
              endPosition.x,
              endPosition.y,
              p
            )
            // Burst out of the payer, then settle to a readable travelling size.
            const scale =
              p < 0.15
                ? gsap.utils.interpolate(2, 5.2, p / 0.15)
                : gsap.utils.interpolate(5.2, 3.4, (p - 0.15) / 0.85)
            // Fade in quickly, hold through the flight, fade out on arrival.
            const opacity =
              p < 0.08 ? p / 0.08 : p > 0.82 ? Math.max(0, 1 - (p - 0.82) / 0.18) : 1
            // Gentle sway that peaks mid-flight and returns to flat.
            const rotation = tilt * Math.sin(p * Math.PI)
            gsap.set(item, {
              x: point.x,
              y: point.y,
              scale,
              opacity,
              rotation,
              force3D: true,
            })
          },
          onStart: () => {
            // One chime per bill, staggered with each bill's launch, so the
            // audio cascade stays in sync with the flying bills.
            playMoneyAnimationSound().catch(console.error)
          },
          onComplete: res,
        })
      })
    })

    Promise.all(promises).then(() => {
      dispatch(removeMoneyAnimation(animationData.id))
    })
  }, [startPosition, endPosition, curvePoint, animationData, dispatch])

  useEffect(() => {
    if (!moneyItemsRef.current.length) return
    if (!svgPathRef.current) return
    if (animationData.started) return
    if (
      !startPosition.x ||
      !startPosition.y ||
      !endPosition.x ||
      !endPosition.y
    )
      return

    startMoneyAnimation()
  }, [startPosition, endPosition, animationData, startMoneyAnimation])

  if (moneyAnimations.length === 0) return null

  // Memoize money items calculation
  const moneyItems = useMemo(() => {
    return dividePriceIntoBills(animationData.money)
  }, [animationData.money])

  // const moneyItems = moneyItemIds.map((id) => moneyAnimationItems[id]);

  return (
    <>
      {/* <div
            className="flex relative justify-center  z-10 flex-col"
            ref={wrapperRef}
         > */}
      {moneyItems.map((moneyItem, i) => (
        <img
          key={`money-${i}-${animationData.id}`}
          src={moneyItem.image}
          ref={(el) => (moneyItemsRef.current[i] = el)}
          className="h-6 w-auto absolute top-0 left-0 z-[30] -my-1 will-change-transform will-change-opacity"
          style={{
            // Force hardware acceleration
            transform: "translate3d(0, 0, 0)",
            backfaceVisibility: "hidden",
            perspective: 1000,
          }}
        />
      ))}
      {/* </div> */}
      <svg className="h-screen w-screen fixed -z-10">
        <path ref={svgPathRef} d={pathString} fill="none"></path>
      </svg>
      {/* <div
        className="fixed right-0 top-0 btn z-10 "
        onClick={startMoneyAnimation}
      >
        Money Animation
      </div> */}
    </>
  )
}

export default MoneyAnimation
